import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);    // null=checking, false=not auth, object=auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await authApi.me();
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (phone, password, ukcNumber) => {
    const { data } = await authApi.login(phone, password, ukcNumber);
    if (data.requiresOtp) {
      // Don't set user yet — caller (LoginPage) will show OTP step
      return { requiresOtp: true, ...data };
    }
    setUser(data.user);
    return data.user;
  };

  const verifyLoginOtp = async (pendingToken, code, trustDevice = true) => {
    const { data } = await authApi.verifyOtp(pendingToken, code, trustDevice);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, phone, password) => {
    const { data } = await authApi.register(name, phone, password);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = async (sessionId) => {
    const { data } = await authApi.googleSession(sessionId);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await authApi.logout(); } catch {}
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await authApi.me();
      setUser(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyLoginOtp, register, loginWithGoogle, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
